/* Ported from the designer's kit wb-codemirror.js (vanilla factory). Two changes
 * for the bundled component: CodeMirror is imported (module singleton) instead of
 * read off window, and both mounts return a uniform handle so the React shell can
 * expose one imperative API for chrome + bare. Loosely typed on purpose — this is
 * vendored DOM glue, not app logic. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import CodeMirror from 'codemirror';
import type { CodeLanguage } from './types';

type Opts = {
  value?: string;
  language?: CodeLanguage;
  readOnly?: boolean;
  filename?: string;
  lineNumbers?: boolean;
  lineWrapping?: boolean;
  placeholder?: string;
};

export interface WBHandle {
  cm: any;
  getValue(): string;
  setValue(v: string): void;
  toFile(): File;
  focus(): void;
}

const MODE_BY_LANG: Record<string, { mode: any; label: string }> = {
  python:     { mode: 'python', label: 'Python 3.10' },
  json:       { mode: { name: 'javascript', json: true }, label: 'JSON' },
  javascript: { mode: 'javascript', label: 'JavaScript' },
  text:       { mode: 'text/plain', label: 'Text' },
};
function resolveLang(language?: string) { return MODE_BY_LANG[language as string] || MODE_BY_LANG.python; }
function fileNameFor(o: Opts) { return o.filename || (o.language === 'json' ? 'evaluation.json' : 'estimator.py'); }
function mimeFor(o: Opts) { return o.language === 'json' ? 'application/json' : 'text/x-python'; }

// ── low-level mount: a bare themed editor, no chrome ────────────────────────
export function WBCmMount(el: HTMLElement, opts: Opts): WBHandle {
  opts = opts || {};
  const lang = resolveLang(opts.language);
  const readOnly = !!opts.readOnly;
  const cm: any = (CodeMirror as any)(el, {
    value: opts.value || '',
    mode: lang.mode,
    theme: 'whestbench',
    lineNumbers: opts.lineNumbers !== false,
    // read-only must stay FOCUSABLE so text selection + Cmd+C work; CodeMirror's
    // 'nocursor' disables focus (and thus copy). Plain `true` blocks edits only.
    readOnly,
    matchBrackets: !readOnly,
    styleActiveLine: !readOnly,
    lineWrapping: !!opts.lineWrapping,
    tabSize: 4,
    indentUnit: 4,
    viewportMargin: Infinity,
    placeholder: opts.placeholder || '',
    cursorBlinkRate: readOnly ? -1 : 530,
  });
  // read-only viewers stay focusable (so select + Cmd+C work); tag the wrapper so
  // CSS can hide the now-unused caret for a clean pure-viewer look.
  if (readOnly) cm.getWrapperElement().classList.add('cm-readonly');
  // lay out once fonts have settled
  setTimeout(() => cm.refresh(), 60);
  const doc: any = typeof document !== 'undefined' ? document : null;
  if (doc && doc.fonts && doc.fonts.ready) doc.fonts.ready.then(() => cm.refresh());
  return {
    cm,
    getValue: () => cm.getValue(),
    setValue: (v: string) => cm.setValue(v),
    toFile: () => new File([cm.getValue()], fileNameFor(opts), { type: mimeFor(opts) }),
    focus: () => cm.focus(),
  };
}

// ── glyphs (verbatim from kit wb-codemirror.js lines 56–59) ─────────────────
const FILE_SVG = '<svg class="f-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
const LOCK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>';
const COPY_SVG = '<span class="copy-glyph" style="display:inline-flex"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg></span><span class="ok-glyph"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>';
const WRAP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><path d="M3 12h15a3 3 0 1 1 0 6h-4"/><polyline points="16 16 14 18 16 20"/><line x1="3" y1="18" x2="10" y2="18"/></svg>';

function mk(tag: string, cls?: string, html?: string): HTMLElement {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (html != null) n.innerHTML = html;
  return n;
}

// ── full self-framing editor (head bar · body · foot) ───────────────────────
export function WBCodeEditor(mount: HTMLElement, opts: Opts): WBHandle {
  opts = opts || {};
  const lang = resolveLang(opts.language);
  const readOnly = !!opts.readOnly;
  const value = opts.value || '';
  const filename = fileNameFor(opts);

  mount.classList.add('wbce');
  mount.classList.toggle('is-readonly', readOnly);

  const head = mk('div', 'wbce-head');
  const file = mk('div', 'wbce-file', FILE_SVG + '<span class="f-name"></span>');
  (file.querySelector('.f-name') as HTMLElement).textContent = filename;
  const dirty = mk('span', 'wbce-dirty');
  const lock = mk('span', 'wbce-lock', LOCK_SVG + 'Read only');
  head.appendChild(file);
  if (!readOnly) head.appendChild(dirty);
  head.appendChild(lock);
  head.appendChild(mk('span', 'spacer'));
  head.appendChild(mk('span', 'wbce-lang', lang.label));

  const actions = mk('div', 'wbce-actions');
  const wrapBtn = mk('button', 'wbce-btn', WRAP_SVG) as HTMLButtonElement;
  wrapBtn.type = 'button'; wrapBtn.title = 'Toggle soft wrap';
  const copyBtn = mk('button', 'wbce-btn', COPY_SVG + '<span class="copy-label">Copy</span>') as HTMLButtonElement;
  copyBtn.type = 'button'; copyBtn.title = 'Copy to clipboard';
  actions.appendChild(wrapBtn); actions.appendChild(copyBtn);
  head.appendChild(actions);

  const body = mk('div', 'wbce-body');
  const foot = mk('div', 'wbce-foot');
  const sLines = mk('span', 'stat', 'Lines <span class="v">0</span>');
  const sEnc = mk('span', 'stat', 'UTF-8 · LF');
  const sCursor = mk('span', 'stat', '<span class="v">Ln 1, Col 1</span>');
  const sMode = mk('span', 'mode ' + (readOnly ? 'readonly' : 'editable'), readOnly ? 'Read only' : 'Editable');
  foot.appendChild(sLines); foot.appendChild(sEnc); foot.appendChild(sCursor);
  foot.appendChild(mk('span', 'spacer')); foot.appendChild(sMode);

  mount.appendChild(head); mount.appendChild(body); mount.appendChild(foot);

  const handle = WBCmMount(body, opts);
  const cm: any = handle.cm;

  function refreshStats() {
    (sLines.querySelector('.v') as HTMLElement).textContent = String(cm.lineCount());
    const c = cm.getCursor();
    (sCursor.querySelector('.v') as HTMLElement).textContent = 'Ln ' + (c.line + 1) + ', Col ' + (c.ch + 1);
  }
  refreshStats();

  if (!readOnly) {
    const baseline = value;
    cm.on('change', () => { mount.classList.toggle('is-dirty', cm.getValue() !== baseline); refreshStats(); });
    cm.on('cursorActivity', refreshStats);
    cm.on('focus', () => mount.classList.add('is-focused'));
    cm.on('blur', () => mount.classList.remove('is-focused'));
  } else {
    cm.on('cursorActivity', refreshStats);
  }

  wrapBtn.addEventListener('click', () => {
    const on = !cm.getOption('lineWrapping');
    cm.setOption('lineWrapping', on);
    wrapBtn.classList.toggle('is-on', on);
  });

  copyBtn.addEventListener('click', () => {
    const text = cm.getValue();
    const done = () => {
      copyBtn.classList.add('copied');
      const lbl = copyBtn.querySelector('.copy-label');
      if (lbl) lbl.textContent = 'Copied';
      setTimeout(() => { copyBtn.classList.remove('copied'); if (lbl) lbl.textContent = 'Copy'; }, 1400);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done, done);
    else done();
  });

  return handle; // getValue/setValue/toFile/focus delegate to the inner cm
}
